import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useAuth0 } from '../../react-auth0-spa';
import { images } from '../../images/images';
import InputField from '../../components/ui/InputField';
import PhoneNumberField from '../../components/ui/PhoneNumberField';
import SelectField from '../../components/ui/SelectField';
import CustomButton from '../../components/CustomButton';
import { COUNTRIES, getCountryLabel } from './countries';
import {
    useLazyGetAccountPermissionsQuery,
    useRegisterTenantMutation,
} from '../../api/account/accountApiSlice';
import { TenantOrganizationType } from '../../api/account/accountApiTypes';
import { useOrganizationTypeOptions } from '../../common/organizationTypes';
import { paths } from '../../routes';
import { Tenant } from '../../api/types';
import {
    tenantSelected,
    userAuthenticated,
} from '../../action/authenticationEvents';
import { toAuthPayload } from '../../common/accountPermissions';
import {
    clearStashedSignupSolution,
    getStashedSignupSolution,
    isSupportedSignupSolution,
    setStoredOrganizationName,
    setStoredSolution,
} from '../../common/selfServiceSolutions';
import UnsupportedSolutionScreen from './UnsupportedSolutionScreen';

const OnboardingPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, logout } = useAuth0();
    const [registerTenant, { isLoading }] = useRegisterTenantMutation();
    const [triggerGetPermissions] = useLazyGetAccountPermissionsQuery();

    const [firstName, setFirstName] = useState(user?.given_name ?? '');
    const [lastName, setLastName] = useState(user?.family_name ?? '');
    const [phone, setPhone] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [organizationType, setOrganizationType] = useState<TenantOrganizationType | ''>('');
    const [country, setCountry] = useState('');
    const [slug, setSlug] = useState('');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Same regex as the backend (SLUG_REGEX in accountController.ts).
    const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
    const slugIsValid = SLUG_REGEX.test(slug);

    // Auto-derive a slug from the organization name until the user types
    // something into the slug field themselves. Saves the most-confusing
    // step in the form ("what do I put here?") for the common case where
    // the org name maps cleanly to a URL-safe identifier.
    //
    // Rules mirror the backend SLUG_REGEX: lowercase a-z 0-9 and hyphens,
    // 3-63 chars, must start/end with alphanumeric.
    //
    // Trailing legal-entity suffixes (GmbH, AG, e.V., Ltd, …) are stripped
    // from the auto-suggestion before slugifying — they're noise in a URL
    // and most users would delete them by hand. The user can still type
    // them into the slug field if they want to.
    // Compound German Personengesellschaft suffixes ("GmbH & Co. KG" and
    // siblings) get stripped as a unit so the trailing "& Co." doesn't
    // survive — "Co" alone is intentionally not in the simple list since
    // it's more often part of a brand ("Friends & Co").
    const COMPOUND_LEGAL_SUFFIX = /[\s,]+(?:gmbh|ag|ug|gug)\s*&?\s*co\.?\s*(?:kg|ohg|kgaa)\.?\s*$/i;
    // Split into smaller alternations to keep each regex below Sonar's
    // complexity threshold. The loop below applies them in sequence so a
    // chain like "Foo Inc. GmbH" still strips to "Foo".
    const LEGAL_SUFFIX_DE = /[\s,&]+(?:gmbh|mbh|ag|kg|ohg|gbr|ug|gug|kgaa)\.?\s*$/i;
    const LEGAL_SUFFIX_INTL = /[\s,&]+(?:inc|ltd|llc|corp|lp|llp|plc|sarl|sa|bv|nv|spa|srl)\.?\s*$/i;
    const LEGAL_SUFFIX_ABBR = /[\s,&]+e\.?\s*[vk]\.?\s*$/i;
    const stripLegalSuffix = (org: string): string => {
        let result = org.trim().replace(COMPOUND_LEGAL_SUFFIX, '').trim();
        let prev = '';
        while (prev !== result) {
            prev = result;
            result = result
                .replace(LEGAL_SUFFIX_DE, '')
                .replace(LEGAL_SUFFIX_INTL, '')
                .replace(LEGAL_SUFFIX_ABBR, '')
                .trim();
        }
        return result;
    };
    // Second pass, on the already-slugified value. Once the name is split on
    // non-alphanumerics every legal form is its own hyphen-delimited token,
    // so we can drop it by whole-token match without the mid-word false
    // positives that force the pre-slugify pass to demand a separator (which
    // is exactly why that pass misses glued forms like "gGmbH"/"gAG" and
    // qualified ones like "UG (haftungsbeschränkt)"). "co" is intentionally
    // absent — it's too often a real brand word ("Friends & Co"); the
    // compound "GmbH & Co. KG" family is handled in the pre-slugify pass.
    const SLUG_LEGAL_TOKENS = new Set([
        'gmbh', 'ggmbh', 'gesmbh', 'mbh', 'ug', 'gug', 'ag', 'gag',
        'kg', 'kgaa', 'ohg', 'og', 'gbr', 'gesbr', 'eg', 'se', 'ev', 'ek',
        'inc', 'ltd', 'llc', 'corp', 'lp', 'llp', 'plc', 'sarl', 'sa',
        'bv', 'nv', 'spa', 'srl',
    ]);
    const stripSlugLegalTokens = (slug: string): string => {
        const tokens = slug.split('-').filter(Boolean);
        // Keep at least one token so an org named only after its legal form
        // doesn't collapse to an empty slug.
        while (tokens.length > 1) {
            const last = tokens[tokens.length - 1];
            // "haftungsbeschränkt" trails "UG" and slugifies to this token.
            if (last === 'haftungsbeschrankt' || SLUG_LEGAL_TOKENS.has(last)) {
                tokens.pop();
            } else {
                break;
            }
        }
        return tokens.join('-');
    };
    const deriveSlugFromOrgName = (org: string): string => stripSlugLegalTokens(
        stripLegalSuffix(org)
            .toLowerCase()
            .replaceAll('ß', 'ss')
            .normalize('NFD')
            .replaceAll(/[̀-ͯ]/g, '')
            .replaceAll(/[^a-z0-9]+/g, '-')
            .replaceAll(/^-+|-+$/g, ''),
    ).slice(0, 63);

    useEffect(() => {
        if (slugManuallyEdited) return;
        const trimmed = organizationName.trim();
        if (!trimmed) {
            setSlug('');
            return;
        }
        const suggested = deriveSlugFromOrgName(trimmed);
        // Only auto-fill if the suggestion is itself valid against the
        // backend regex; partial typing ("ab" → 2 chars) leaves the
        // field empty until the user reaches a usable suggestion.
        setSlug(SLUG_REGEX.test(suggested) ? suggested : '');
    }, [organizationName, slugManuallyEdited]);
    // Non-blocking nudge only: the slug is immutable once confirmed, so a
    // legal form baked into it would outlive a later change of legal form.
    // The auto-suggestion never contains one, so this only ever fires when
    // the user types it back in by hand.
    const slugHasLegalSuffix = slug.length > 0 && stripSlugLegalTokens(slug) !== slug;

    const isValid = firstName.trim()
        && lastName.trim()
        && organizationName.trim()
        && organizationType
        && country
        && slugIsValid;

    const handleSubmit = async () => {
        if (!isValid || !organizationType) return;
        setErrorMessage(null);

        try {
            // Trial-solution preset: stashed by App.tsx on /signup from the
            // marketing URL (?solution=…). Backend defaults to 'room' if
            // omitted; sending it explicitly persists the chosen solution
            // on the tenant row.
            const signupSolution = getStashedSignupSolution() ?? undefined;
            const result = await registerTenant({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: phone.trim() || undefined,
                organizationName: organizationName.trim(),
                organizationType,
                country,
                slug,
                email: user?.email,
                solution: signupSolution,
            }).unwrap();

            // Cache the registered solution and organisation name so
            // App.tsx synthesises the matching services and brand text on
            // this and future sessions, then drop the signup-time stash.
            setStoredSolution(result.tenant.solution);
            setStoredOrganizationName(result.tenant.organizationName);
            clearStashedSignupSolution();

            // Register has created the owner app_user row, so load the UI
            // permission set from our DB (the single source of truth) rather
            // than a hardcoded owner-grade constant (#1377).
            try {
                const perms = await triggerGetPermissions().unwrap();
                dispatch(userAuthenticated(toAuthPayload(perms)));
            } catch {
                // Transient failure: empty grant so permissionsLoaded still
                // flips; App.tsx's auth effect re-fetches on the next render.
                dispatch(userAuthenticated({ permissions: [], roles: [] }));
            }
            // Use the registered tenant's real slug as the Redux tenant
            // identifier — that becomes the X-Tenant header on every
            // subsequent request and the backend will route us to the
            // operational DB by slug.
            dispatch(tenantSelected({ tenant: (result.tenant.slug ?? slug) as Tenant }));
            navigate(paths.dashboard, { replace: true });
        } catch (err: unknown) {
            // axiosBaseQuery formats errors as { status, data }
            const e = err as { status?: number, data?: { message?: string } | string };
            const message = typeof e?.data === 'object' && e?.data?.message
                ? e.data.message
                : t('onboarding.error_generic');
            setErrorMessage(message);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isValid && !isLoading) {
            handleSubmit();
        }
    };

    // "Sign out and use a different account" — for users who realise
    // mid-form that they should onboard with a different email (e.g.
    // their business address vs. the personal one they signed up with).
    // We deliberately don't clear the signup-solution stash here: the
    // user wants to swap the Auth0 identity but stay on the same kolula
    // ROOM trial they entered through, so the next session should pick
    // up at the form again rather than bouncing them through the
    // marketing site. Auth0's logout returns the user to the app
    // origin; App.tsx then triggers a login redirect and, post-login,
    // TenantBootstrap routes the no-tenant user back to /onboarding.
    const handleLogout = () => {
        logout({ logoutParams: { returnTo: window.location.origin } });
    };

    const lang = i18n.language;
    const countryOptions = COUNTRIES.map((c) => c.code);
    const countryLabels = Object.fromEntries(
        COUNTRIES.map((c) => [c.code, getCountryLabel(c.code, lang)]),
    );
    const orgType = useOrganizationTypeOptions();

    return (
        <div className={'min-h-screen bg-[rgb(var(--color-default-background))] flex items-center justify-center px-4'}>
            <div className={'w-full max-w-md'} onKeyDown={handleKeyDown}>
                <div className={'flex flex-col items-center mb-8'}>
                    <img src={images.kolulaLogo} alt={'Kolula'} className={'h-10 mb-6'} />
                    <h1 className={'text-2xl font-semibold text-foreground text-center'}>
                        {t('onboarding.welcome_title')}
                    </h1>
                    <p className={'text-sm text-foreground/80 mt-2 text-center max-w-sm'}>
                        {t('onboarding.welcome_subtitle')}
                    </p>
                </div>

                <div className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
                    {user?.email && (
                        <p className={'text-sm text-foreground/60 mb-6'}>
                            {t('onboarding.signed_in_as')}{' '}
                            <span className={'font-medium text-foreground'}>{user.email}</span>
                        </p>
                    )}

                    <div className={'flex flex-col gap-5'}>
                        <p className={'text-base font-semibold text-foreground'}>
                            {t('onboarding.personal_section')}
                        </p>
                        <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                            <InputField
                                label={t('onboarding.first_name')}
                                value={firstName}
                                onChange={setFirstName}
                            />
                            <InputField
                                label={t('onboarding.last_name')}
                                value={lastName}
                                onChange={setLastName}
                            />
                        </div>
                        <div className={'flex flex-col gap-1'}>
                            <PhoneNumberField
                                label={t('onboarding.phone')}
                                value={phone}
                                onChange={setPhone}
                                defaultCountry={country || (i18n.language?.toLowerCase().startsWith('de') ? 'DE' : 'US')}
                                labelAbove
                            />
                            <p className={'text-xs text-foreground/60 px-1'}>
                                {t('onboarding.phone_hint')}
                            </p>
                        </div>

                        <p className={'text-base font-semibold text-foreground mt-2'}>
                            {t('onboarding.organization_section')}
                        </p>
                        <div className={'flex flex-col gap-1'}>
                            <InputField
                                label={t('onboarding.organization_name')}
                                value={organizationName}
                                onChange={setOrganizationName}
                            />
                            <p className={'text-xs text-foreground/60 px-1'}>
                                {t('onboarding.organization_name_hint')}
                            </p>
                        </div>
                        <div className={'flex flex-col gap-1'}>
                            <SelectField
                                label={t('organization_type.label')}
                                value={organizationType}
                                onChange={(value) => setOrganizationType(value as TenantOrganizationType)}
                                options={orgType.options}
                                optionLabels={orgType.optionLabels}
                                contentClassName={'w-[var(--radix-select-trigger-width)]'}
                            />
                            <p className={'text-xs text-foreground/60 px-1'}>
                                {t('organization_type.hint')}
                            </p>
                        </div>
                        <SelectField
                            label={t('onboarding.country')}
                            value={country}
                            onChange={setCountry}
                            options={countryOptions}
                            optionLabels={countryLabels}
                            contentClassName={'w-[var(--radix-select-trigger-width)]'}
                            viewportClassName={
                                'p-1 max-h-[min(18rem,var(--radix-select-content-available-height))]'
                            }
                        />

                        <div className={'flex flex-col gap-1'}>
                            <InputField
                                label={t('onboarding.slug')}
                                value={slug}
                                onChange={(value) => {
                                    setSlugManuallyEdited(true);
                                    setSlug(value.toLowerCase());
                                }}
                                placeholder={t('onboarding.slug_placeholder')}
                                labelAbove
                                prefix={'app.kolula.com/'}
                            />
                            <p className={'text-xs text-foreground/60 px-1'}>
                                {t('onboarding.slug_hint')}
                            </p>
                            {slug.length > 0 && !slugIsValid && (
                                <p className={'text-xs text-red-600 px-1'}>
                                    {t('onboarding.slug_format_error')}
                                </p>
                            )}
                            {slugIsValid && slugHasLegalSuffix && (
                                <p className={'text-xs text-amber-700 px-1'}>
                                    {t('onboarding.slug_legal_suffix_warning')}
                                </p>
                            )}
                        </div>
                    </div>

                    {errorMessage && (
                        <div
                            role={'alert'}
                            className={'mt-6 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700'}
                        >
                            {errorMessage}
                        </div>
                    )}

                    <div className={'mt-8'}>
                        <CustomButton
                            title={t('onboarding.get_started')}
                            onClick={handleSubmit}
                            disabled={!isValid}
                            loading={isLoading}
                            fullWidth
                        />
                    </div>

                    <div className={'mt-4 flex justify-center'}>
                        <button
                            type={'button'}
                            onClick={handleLogout}
                            className={'text-sm text-foreground/60 hover:text-foreground transition-colors'}
                        >
                            {t('onboarding.logout_cta')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Post-Auth0 belt to SignupGate's pre-Auth0 suspenders. A user can
// technically land on /onboarding without coming through /signup
// (direct URL, bookmarked link, sessionStorage cleared mid-flow).
// The stashed signup solution is our only reliable signal at this
// point; if it's missing or unsupported we refuse to mount the form
// so a half-onboarded tenant for an unsupported solution can't be
// created. Implemented as a wrapper so the inner OnboardingPage's
// hook order isn't conditional on the gate result.
const OnboardingPageRoute = () => {
    const stashedSolution = getStashedSignupSolution();
    if (!isSupportedSignupSolution(stashedSolution)) {
        return <UnsupportedSolutionScreen requested={stashedSolution} />;
    }
    return <OnboardingPage />;
};

export default OnboardingPageRoute;
